import {
  EMapProvider,
  EMinimumAndroidVersion,
  IAndroidConfig,
  ICommonSettings,
  IImageLibraryDto,
  ILanguageModel
} from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import { PureComponent, ReactNode } from "react";
import OrientationAnySvg from "../../../../svgs/orientation-any.svg";
import OrientationLandscapeSvg from "../../../../svgs/orientation-landscape.svg";
import OrientationPortraitSvg from "../../../../svgs/orientation-portrait.svg";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import { ConfigAdiRegistrationInput } from "./app-settings-config-adi-registration/app-settings-config-adi-registration-input";
import { ConfigBluetoothInput } from "./app-settings-config-bluetooth/app-settings-config-bluetooth-input";
import { ConfigCameraInput } from "./app-settings-config-camera/app-settings-config-camera.input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "./app-settings-config-container";
import { FavIconField } from "./app-settings-config-favicon/app-settings-config-favicon-input";
import { ConfigFirebaseInput } from "./app-settings-config-firebase/app-settings-config-firebase-input";
import { ConfigGenerationTypeInput } from "./app-settings-config-generation-type/app-settings-config-generation-type-input";
import { GooglePlayConnectApiInput } from "./app-settings-config-google-play/app-settings-config-google-play-input";
import { GoogleRecaptchaAndroidInput } from "./app-settings-config-google-recaptcha/app-settings-config-google-recaptcha-android-input";
import { ConfigGpsTrackingBackgroundPermissionInput } from "./app-settings-config-gps-tracker/app-settings-config-gps-tracker-background-permission.-input";
import { ConfigGpsTrackingInput } from "./app-settings-config-gps-tracker/app-settings-config-gps-tracker-input";
import { ConfigGpsTrackingPermissionInput } from "./app-settings-config-gps-tracker/app-settings-config-gps-tracker-permission-input";
import { ConfigIsHapticEnabled } from "./app-settings-config-is-haptic/app-settings-config-is-haptic-enabled-input";
import { ConfigLocationInput } from "./app-settings-config-location/app-settings-config-location-input";
import { ConfigAMapInput } from "./app-settings-config-maps/app-settings-config-amap-input";
import { ConfigGoogleMapsInput } from "./app-settings-config-maps/app-settings-config-google-maps-input";
import { ConfigMapProviderSelect } from "./app-settings-config-maps/app-settings-config-map-provider-select";
import { ConfigNotificationPermissionInput } from "./app-settings-config-permission-text/app-settings-config-permission-text-input";
import { SplashInput } from "./app-settings-config-splash/app-settings-config-splash-inputs";
import { ANDROID_CATEGORIES, IConfigCategory, visibleCategories } from "./config-categories";
import { ConfigField, ConfigSection, ConfigSupportToggle } from "./config-pane-parts";
import {
  ConfigCategoryStatus,
  ConfigDifference,
  ConfigPaneShell,
  ConfigRestorableValue,
  getOverriddenCategoryIds,
  readCategoryInheritance,
  restoreCategory
} from "./config-pane-shell";

/**
 * The API levels a generated Android app may target. Each option carries its own id
 * because the E2E suite picks a level by name, not by position.
 */
/** What bundling both map SDKs costs an Android app; shown while end users may choose the provider. */
const ANDROID_MAP_PROVIDER_CHOICE_HINT: string =
  "Both map SDKs are bundled: the app grows by about 31 MB (less per device when published as an App Bundle), and the Amap SDK must be declared in Google Play's Data safety form.";

const MINIMUM_ANDROID_VERSIONS: KuikaSelectboxOption[] = [
  {
    value: EMinimumAndroidVersion.Android8,
    searchLabel: "Android 8",
    label: "Android 8",
    testId: "Android8"
  },
  {
    value: EMinimumAndroidVersion.Android9,
    searchLabel: "Android 9",
    label: "Android 9",
    testId: "Android9"
  },
  {
    value: EMinimumAndroidVersion.Android10,
    searchLabel: "Android 10",
    label: "Android 10",
    testId: "Android10"
  },
  {
    value: EMinimumAndroidVersion.Android11,
    searchLabel: "Android 11",
    label: "Android 11",
    testId: "Android11"
  },
  {
    value: EMinimumAndroidVersion.Android12,
    searchLabel: "Android 12",
    label: "Android 12",
    testId: "Android12"
  },
  {
    value: EMinimumAndroidVersion.Android13,
    searchLabel: "Android 13",
    label: "Android 13",
    testId: "Android13"
  }
];

const ANDROID_ORIENTATIONS: KuikaSelectboxOption[] = [
  {
    value: "any",
    searchLabel: "All orientation",
    label: (
      <span className="kuika-config-option">
        <OrientationAnySvg />
        <span data-testid="androidAllOrientationOption">All orientation</span>
      </span>
    )
  },
  {
    value: "portrait",
    searchLabel: "Portrait",
    label: (
      <span className="kuika-config-option">
        <OrientationPortraitSvg />
        <span data-testid="androidPortraitOption">Portrait</span>
      </span>
    )
  },
  {
    value: "landscape",
    searchLabel: "Landscape",
    label: (
      <span className="kuika-config-option">
        <OrientationLandscapeSvg />
        <span data-testid="androidLandscapeOption">Landscape</span>
      </span>
    )
  }
];

const ANDROID_DEVICES: KuikaSelectboxOption[] = [
  {
    value: 0,
    searchLabel: "All",
    label: (
      <span className="kuika-config-option">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M15.6248 17.2915H11.0415C10.3514 17.2915 9.7915 16.7316 9.7915 16.0415V7.2915C9.7915 6.6014 10.3514 6.0415 11.0415 6.0415H15.6248C16.3149 6.0415 16.8748 6.6014 16.8748 7.2915V16.0415C16.8748 16.7316 16.3149 17.2915 15.6248 17.2915Z"
            stroke="currentColor"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.9585 14.7915H12.7085"
            stroke="currentColor"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.33333 13.9583H4.375C3.6849 13.9583 3.125 13.3984 3.125 12.7083V3.95825C3.125 3.26815 3.6849 2.70825 4.375 2.70825H12.2917C12.9818 2.70825 13.5417 3.26815 13.5417 3.95825V4.58325"
            stroke="currentColor"
            strokeMiterlimit="10"
            strokeLinejoin="round"
          />
          <path
            d="M8.33317 10.8333H7.2915C6.94645 10.8333 6.6665 11.1132 6.6665 11.4583C6.6665 11.8033 6.94645 12.0833 7.2915 12.0833H8.33317V10.8333Z"
            fill="currentColor"
          />
        </svg>
        <span data-testid="androidDevicesAllOption">All</span>
      </span>
    )
  },
  {
    value: 1,
    searchLabel: "Phones",
    label: (
      <span className="kuika-config-option">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M13.125 17.7084H6.875C6.1849 17.7084 5.625 17.1485 5.625 16.4584V3.54175C5.625 2.85164 6.1849 2.29175 6.875 2.29175H13.125C13.8151 2.29175 14.375 2.85164 14.375 3.54175V16.4584C14.375 17.1485 13.8151 17.7084 13.125 17.7084Z"
            stroke="currentColor"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M11.0418 15.2085H8.9585"
            stroke="currentColor"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.625 4.79175C10.625 5.1368 10.3451 5.41675 10 5.41675C9.65495 5.41675 9.375 5.1368 9.375 4.79175C9.375 4.4467 9.65495 4.16675 10 4.16675C10.3451 4.16675 10.625 4.4467 10.625 4.79175Z"
            fill="currentColor"
          />
        </svg>
        <span data-testid="androidPhonesOption">Phones</span>
      </span>
    )
  },
  {
    value: 2,
    searchLabel: "Tablets",
    label: (
      <span className="kuika-config-option">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M17.2918 5.20825V14.7916C17.2918 15.4817 16.7319 16.0416 16.0418 16.0416H3.9585C3.26839 16.0416 2.7085 15.4817 2.7085 14.7916V5.20825C2.7085 4.51815 3.26839 3.95825 3.9585 3.95825H16.0418C16.7319 3.95825 17.2918 4.51815 17.2918 5.20825Z"
            stroke="currentColor"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.8335 10C5.8335 10.3451 5.55355 10.625 5.2085 10.625C4.86344 10.625 4.5835 10.3451 4.5835 10C4.5835 9.65495 4.86344 9.375 5.2085 9.375C5.55355 9.375 5.8335 9.65495 5.8335 10Z"
            fill="currentColor"
          />
        </svg>
        <span data-testid="androidTabletsOption">Tablets</span>
      </span>
    )
  }
];

export interface IAndroidConfigProps {
  /**
   * The container's whole parent/child difference, not just this pane's slice: a
   * category can own a key that lives on another one, and Restore writes it by its
   * full path. The rows below read `parentChildDifferences`, which narrows it.
   */
  parentChildDifference?: ConfigDifference;
  /** Form paths edited since the configuration was opened — the rail's dots and the orange labels. */
  changedPaths?: ReadonlySet<string>;
  /** Puts the named paths back to what they were when the configuration was opened. */
  onRevertPaths?: (paths: string[]) => void;
  /** True only when this configuration inherits from a workspace one. */
  hasParentConfig?: boolean;
  isGlobalConfig?: boolean;
  allImage?: IImageLibraryDto[];
  handleGetImageLibrary: () => void;
  languages: ILanguageModel[];
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput: (formItemName: string[], value: ConfigRestorableValue) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
  isSupporting: boolean;
  toggleSupport: () => void;
}

export interface IAndroidConfigState {
  /** The rail's selection. Pane-local, so it survives a trip through another platform tab. */
  activeCategoryId: string;
}

interface IAndroidSection {
  description?: ReactNode;
  titleTestId?: string;
  descriptionTestId?: string;
  body: ReactNode;
}

/** The categories a workspace configuration has nothing to say about. */
const GLOBAL_HIDDEN_CATEGORIES: string[] = [
  "androidMobileAppSettings",
  "androidGeolocation",
  "androidCamera",
  "androidBluetooth",
  "androidGpsTracker"
];

export class AndroidConfig extends PureComponent<IAndroidConfigProps, IAndroidConfigState> {
  constructor(props: IAndroidConfigProps) {
    super(props);
    this.state = { activeCategoryId: ANDROID_CATEGORIES[0].id };
  }

  getCategories = (): IConfigCategory[] =>
    visibleCategories(ANDROID_CATEGORIES, this.props.isGlobalConfig ? GLOBAL_HIDDEN_CATEGORIES : []);

  /** The selection, corrected for a category that has gone away under it. */
  getActiveCategory = (): IConfigCategory => {
    const categories: IConfigCategory[] = this.getCategories();
    const selected: IConfigCategory | undefined = categories.find(
      (category: IConfigCategory): boolean => category.id === this.state.activeCategoryId
    );
    return selected ?? categories[0];
  };

  handleSelectCategory = (categoryId: string): void => {
    this.setState({ activeCategoryId: categoryId });
  };

  /**
   * This pane's own slice of the difference — what the per-row restore affordances read.
   * A row only ever overrides a key on the slice it writes to.
   */
  get parentChildDifferences(): IAndroidConfig | undefined {
    return this.props.parentChildDifference?.androidConfig as IAndroidConfig | undefined;
  }

  handleRestoreCategory = (): void => {
    restoreCategory(
      this.getActiveCategory(),
      this.props.parentChildDifference,
      "androidConfig",
      this.props.handleUpdateCustomFormInput
    );
  };

  renderMobileAppSettings = (): ReactNode => (
    <>
      <ConfigField label="App Name" required tooltipContentKey="android_mobileappsettings_appname">
        <Form.Item
          name={["androidConfig", "appName"]}
          rules={[{ required: this.props.isSupporting, message: "Android App Name is required" }]}
        >
          <KuikaInput size="md" data-testid="androidAppNameInput" placeholder="MyApp" />
        </Form.Item>
      </ConfigField>

      <ConfigField label="Bundle ID" required tooltipContentKey="android_mobileappsettings_bundleid">
        <Form.Item
          name={["androidConfig", "appBundleId"]}
          rules={[
            { required: this.props.isSupporting, message: "Android App Bundle ID is required" },
            {
              // The Bundle ID becomes the Gradle namespace/applicationId and every generated Kotlin
              // package declaration, so it must be a valid Java package name: at least two
              // period-separated segments, each starting with a letter. Hyphens break the Android build
              // ("'my-workspace' is not a valid Java identifier") and are rejected by Google Play.
              pattern: /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/,
              message:
                "Android App Bundle ID must be a valid Java package name: at least two period-separated segments, each starting with a letter and containing only letters, digits, and underscores. Hyphens are not allowed."
            }
          ]}
        >
          <KuikaInput size="md" data-testid="androidBundleIdInput" placeholder="com.workspace.project_name" />
        </Form.Item>
      </ConfigField>

      <ConfigField label="Version" required tooltipContentKey="android_mobileappsettings_version">
        <Form.Item
          name={["androidConfig", "appVersion"]}
          rules={[
            { required: this.props.isSupporting, message: "Android App Version is required" },
            { pattern: /^(\d+\.)?(\d+\.)?(\*|\d+)$/g, message: "Please enter a valid Android App Version" }
          ]}
        >
          <KuikaInput size="md" data-testid="androidVersionInput" placeholder="1.0.0" style={{ marginBottom: 0 }} />
        </Form.Item>
      </ConfigField>

      <ConfigField label="App Version Code" required tooltipContentKey="android_mobileappsettings_versioncode">
        <Form.Item
          name={["androidConfig", "versionCode"]}
          rules={[
            { required: this.props.isSupporting, message: "Android App Version Code is required" },
            { type: "integer", message: "Android App Version Code should be decimal" }
          ]}
        >
          <KuikaInput size="md" type="number" min={0} data-testid="androidVersionCodeInput" />
        </Form.Item>
      </ConfigField>

      <ConfigField
        label="Minimum Android Version"
        required
        tooltipContentKey="android_mobileappsettings_minimumandroidversion"
      >
        <Form.Item
          name={["androidConfig", "minimumAndroidVersion"]}
          rules={[{ required: this.props.isSupporting, message: "Minimum Android Version is required" }]}
        >
          <KuikaSelectbox size="md" data-testid="minimumAndroidVersionInput" options={MINIMUM_ANDROID_VERSIONS} />
        </Form.Item>
      </ConfigField>

      <ConfigField label="Use Cleartext Traffic" tooltipContentKey="android_mobileappsettings_usecleartexttraffic">
        <Form.Item className="switch-form-item" name={["androidConfig", "useClearTextTraffic"]} valuePropName="checked">
          <KuikaSwitch dataTestId="androidUseCleartextTrafficSwitch" />
        </Form.Item>
      </ConfigField>

      <ConfigField label="Trust All Certificates" tooltipContentKey="android_mobileappsettings_trustallcerticates">
        <Form.Item className="switch-form-item" name={["androidConfig", "trustAllCerts"]} valuePropName="checked">
          <KuikaSwitch dataTestId="androidTrustAllCertificatesSwitch" />
        </Form.Item>
      </ConfigField>

      <ConfigField label="Secure Screen Mode" tooltipContentKey="android_mobileappsettings_securescreenmode">
        <Form.Item
          className="switch-form-item"
          name={["androidConfig", "isSecureScreenModeEnabled"]}
          valuePropName="checked"
        >
          <KuikaSwitch dataTestId="androidSecureScreenModeSwitch" />
        </Form.Item>
      </ConfigField>

      <ConfigIsHapticEnabled parentData="androidConfig" />
    </>
  );

  getSections = (): Record<string, IAndroidSection> => ({
    androidMobileAppSettings: {
      description: this.props.getSectionDescription("android_mobileappsettings_description"),
      titleTestId: "androidMobileAppSettingsTitle",
      descriptionTestId: "androidMobileAppSettingsDescription",
      body: this.renderMobileAppSettings()
    },
    androidPlayStore: {
      description: this.props.getSectionDescription("android_playstoresettings_description"),
      titleTestId: "playstoresettingsTitle",
      descriptionTestId: "playStoreSettingsDescription",
      body: (
        <>
          <GooglePlayConnectApiInput
            parentConfigValue={this.parentChildDifferences?.googlePlayApiSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />
          <ConfigAdiRegistrationInput
            parentData="androidConfig"
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />
        </>
      )
    },
    androidGeolocation: {
      description: this.props.getSectionDescription("android_location_description"),
      titleTestId: "androidGeolocationTitle",
      descriptionTestId: "androidGeolocationDescription",
      body: (
        <ConfigLocationInput
          parentData="androidConfig"
          languages={this.props.languages}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    androidCamera: {
      description: this.props.getSectionDescription("android_camera_description"),
      titleTestId: "androidCameraTitle",
      descriptionTestId: "androidCameraDescription",
      body: (
        <ConfigCameraInput
          parentData="androidConfig"
          languages={this.props.languages}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    androidBluetooth: {
      description: this.props.getSectionDescription("android_bluetooth_description"),
      titleTestId: "androidBluetoothTitle",
      descriptionTestId: "androidBluetoothDescription",
      body: (
        <ConfigBluetoothInput
          parentData="androidConfig"
          languages={this.props.languages}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    androidGpsTracker: {
      description: this.props.getSectionDescription("android_gpstracker_description"),
      titleTestId: "androidGpsTrackerTitle",
      descriptionTestId: "androidGpsTrackerDescription",
      body: (
        <>
          <ConfigGpsTrackingInput
            parentData="androidConfig"
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />
          <ConfigGpsTrackingPermissionInput
            parentData="androidConfig"
            languages={this.props.languages}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />
          <ConfigGpsTrackingBackgroundPermissionInput
            parentData="androidConfig"
            languages={this.props.languages}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />
        </>
      )
    },
    androidMaps: {
      description: this.props.getSectionDescription("android_maps_description"),
      titleTestId: "androidMapsTitle",
      descriptionTestId: "androidMapsDescription",
      body: (
        <ConfigMapProviderSelect
          parentData="androidConfig"
          tooltipContentKey="android_maps_provider_info"
          supportsEndUserChoice
          endUserChoiceHint={ANDROID_MAP_PROVIDER_CHOICE_HINT}
          renderProvider={(provider: EMapProvider) =>
            provider === EMapProvider.Amap ? (
              <ConfigAMapInput
                parentData="androidConfig"
                parentConfigValue={this.parentChildDifferences?.amapSettingsId}
                commonSettings={this.props.commonSettings}
                getSectionDescription={this.props.getSectionDescription}
                handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
                handleCreateCommonConfig={this.props.handleCreateCommonConfig}
                handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
                handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
              />
            ) : (
              <ConfigGoogleMapsInput
                parentData="androidConfig"
                parentConfigValue={this.parentChildDifferences?.googleMapsSettingsId}
                commonSettings={this.props.commonSettings}
                getSectionDescription={this.props.getSectionDescription}
                handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
                handleCreateCommonConfig={this.props.handleCreateCommonConfig}
                handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
                handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
              />
            )
          }
        />
      )
    },
    androidPushNotification: {
      description: this.props.getSectionDescription("android_pushnotification_description"),
      titleTestId: "androidPushNotificationTitle",
      descriptionTestId: "androidPushNotificationDescription",
      body: (
        <>
          <ConfigFirebaseInput
            parentData="androidConfig"
            parentConfigValue={this.parentChildDifferences?.firebaseSettingsId}
            commonSettings={this.props.commonSettings}
            getSectionDescription={this.props.getSectionDescription}
            handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
            handleCreateCommonConfig={this.props.handleCreateCommonConfig}
            handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
            handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
          />
          {/* The permission prompt's wording is a project's copy, not a workspace default. */}
          {!this.props.isGlobalConfig && (
            <ConfigNotificationPermissionInput
              parentData="androidConfig"
              languages={this.props.languages}
              commonSettings={this.props.commonSettings}
              getSectionDescription={this.props.getSectionDescription}
              handleCreateCommonConfig={this.props.handleCreateCommonConfig}
              handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
              handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
            />
          )}
        </>
      )
    },
    androidGoogleRecaptcha: {
      description: this.props.getSectionDescription("androidconfig_googlerecaptcha_description"),
      titleTestId: "androidGoogleRecaptchaTitle",
      body: (
        <GoogleRecaptchaAndroidInput
          parentConfigValue={this.parentChildDifferences?.googleRecaptchaAndroidSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    }
  });

  /** Orientation and device family: the app's shape, not any one category's setting. */
  renderOptions = (): ReactNode => (
    <ConfigSection rail title="Options" titleTestId="androidOptionsTitle">
      <ConfigField
        aside
        label="Orientation"
        labelTestId="androidOrientationText"
        tooltipContentKey="android_options_orientation"
      >
        <Form.Item name={["androidConfig", "orientation"]}>
          <KuikaSelectbox size="md" data-testid="androidOrientationDropdown" options={ANDROID_ORIENTATIONS} />
        </Form.Item>
      </ConfigField>

      <ConfigField aside label="Devices" labelTestId="androidDevicesText" tooltipContentKey="android_options_devices">
        <Form.Item name={["androidConfig", "deviceType"]}>
          <KuikaSelectbox size="md" data-testid="androidDevicesDropdown" options={ANDROID_DEVICES} />
        </Form.Item>
      </ConfigField>
    </ConfigSection>
  );

  renderAside = (): ReactNode => {
    const { isGlobalConfig } = this.props;

    return (
      <>
        {!isGlobalConfig && (
          <ConfigSupportToggle
            label="Support Android UI"
            description={this.props.getSectionDescription("android_supportandroid_description")}
            checked={this.props.isSupporting}
            onChange={this.props.toggleSupport}
            switchTestId="supportAndroidSwitch"
            descriptionTestId="supportAndroidDescription"
            labelTestId="supportAndroidText"
            aside
          />
        )}

        {/* Decides which generator the whole tab is compiled into, so it sits with the platform's
            support switch rather than under one category. The control owns its section and hides it —
            for everyone while React Native is on hold, and outside the closed beta otherwise (see
            `ConfigGenerationTypeInput`). Keep the Form.Item mounted either way: save rebuilds the
            configuration from the mounted fields, so dropping it would drop the saved value. */}
        {!isGlobalConfig && this.props.isSupporting && (
          <Form.Item name="androidGenerationType" noStyle>
            <ConfigGenerationTypeInput
              platform="android"
              sectionDescription={
                this.props.getSectionDescription("appsettings_mobilegenerationtype_description") ??
                "Choose how this configuration's Android application is generated. Native produces the Android (Kotlin) project. React Native produces an Expo project, shared with iOS when iOS is set to React Native too."
              }
            />
          </Form.Item>
        )}

        {this.props.hasParentConfig && (
          <ConfigCategoryStatus
            inheritance={readCategoryInheritance(
              this.getActiveCategory(),
              this.props.parentChildDifference,
              "androidConfig"
            )}
            onRestore={this.handleRestoreCategory}
          />
        )}

        {!isGlobalConfig && (
          <>
            {this.renderOptions()}

            <ConfigSection
              rail
              title="App Icons"
              titleTestId="androidAppIconsTitle"
              description="32x32 px and PNG file"
            >
              {/* Android ships a round launcher icon alongside the square one, and the two
                  pickers are otherwise identical — without the labels the only thing telling
                  them apart is the shape of a 48px thumbnail. */}
              <ConfigField aside label="Square">
                <Form.Item name={["androidConfig", "iconFilePath"]} noStyle>
                  <FavIconField
                    acceptTpye=".png"
                    allImage={this.props.allImage}
                    handleGetImageLibrary={() => {
                      this.props.handleGetImageLibrary();
                    }}
                  />
                </Form.Item>
              </ConfigField>
              <ConfigField aside label="Round">
                <Form.Item name={["androidConfig", "iconRoundedFileName"]} noStyle>
                  <FavIconField
                    acceptTpye=".png"
                    isRounded
                    allImage={this.props.allImage}
                    handleGetImageLibrary={() => {
                      this.props.handleGetImageLibrary();
                    }}
                  />
                </Form.Item>
              </ConfigField>
            </ConfigSection>

            <ConfigSection rail title="Splash" titleTestId="androidSplashText">
              {/* Registration only. The picker below writes these three through
                  `handleUpdateCustomFormInput`, and a value with no named `Form.Item` is absent
                  from `onFinish` — the splash would mark as changed and then silently not save. */}
              <Form.Item name={["androidConfig", "splashScreenImageName"]} hidden>
                <input type="hidden" />
              </Form.Item>
              <Form.Item name={["androidConfig", "splashScreenBackcolor"]} hidden>
                <input type="hidden" />
              </Form.Item>
              <Form.Item name={["androidConfig", "imageFit"]} hidden>
                <input type="hidden" />
              </Form.Item>
              {/* Explicit path: the control below is a `shouldUpdate` watcher, not a
                  `Form.Item` with a `name`, so `readFieldPath`'s discovery walk finds nothing
                  to register on its own. */}
              <ConfigField
                aside
                label="Image"
                labelTestId="androidImageText"
                hint="PNG and max file size 300KB"
                formInputName={["androidConfig", "splashScreenImageName"]}
              >
                <Form.Item
                  shouldUpdate={(prev, current): boolean =>
                    prev.androidConfig?.splashScreenImageName !== current.androidConfig?.splashScreenImageName ||
                    prev.androidConfig?.splashScreenBackcolor !== current.androidConfig?.splashScreenBackcolor ||
                    prev.androidConfig?.imageFit !== current.androidConfig?.imageFit
                  }
                  noStyle
                >
                  {({ getFieldValue }: FormInstance): ReactNode => (
                    <SplashInput
                      parentData="androidConfig"
                      allImage={this.props.allImage}
                      splashImage={getFieldValue(["androidConfig", "splashScreenImageName"])}
                      splashBgColor={getFieldValue(["androidConfig", "splashScreenBackcolor"])}
                      splashFitting={getFieldValue(["androidConfig", "imageFit"])}
                      handleGetImageLibrary={() => {
                        this.props.handleGetImageLibrary();
                      }}
                      onImageChange={(image: string | null): void =>
                        this.props.handleUpdateCustomFormInput(["androidConfig", "splashScreenImageName"], image)
                      }
                      onColorChange={(color: string): void =>
                        this.props.handleUpdateCustomFormInput(["androidConfig", "splashScreenBackcolor"], color)
                      }
                      onFittingChange={(fitting: "default" | "stretch"): void =>
                        this.props.handleUpdateCustomFormInput(["androidConfig", "imageFit"], fitting)
                      }
                    />
                  )}
                </Form.Item>
              </ConfigField>
            </ConfigSection>
          </>
        )}
      </>
    );
  };

  render() {
    const categories: IConfigCategory[] = this.getCategories();
    const sections: Record<string, IAndroidSection> = this.getSections();
    const activeCategoryId: string = this.getActiveCategory().id;

    return (
      <ConfigPaneShell
        changedPaths={this.props.changedPaths}
        onRevertPaths={this.props.onRevertPaths}
        categories={categories}
        activeCategoryId={activeCategoryId}
        onSelectCategory={this.handleSelectCategory}
        overriddenCategoryIds={getOverriddenCategoryIds(categories, this.props.parentChildDifference, "androidConfig")}
        bodyVisible={this.props.isSupporting}
        aside={this.renderAside()}
      >
        {/* Every category stays mounted; `ConfigSection` hides the ones the rail is not
            pointing at. One antd Form spans all of them and save reads `getFieldsValue()`,
            so a category taken out of the tree is a field written back empty. */}
        {categories.map((category: IConfigCategory) => (
          <ConfigSection
            key={category.id}
            categoryId={category.id}
            active={category.id === activeCategoryId}
            title={category.label}
            description={sections[category.id]?.description}
            titleTestId={sections[category.id]?.titleTestId}
            descriptionTestId={sections[category.id]?.descriptionTestId}
          >
            {sections[category.id]?.body}
          </ConfigSection>
        ))}
      </ConfigPaneShell>
    );
  }
}
